// TODO temporarily borrowed from helpdesk, if this is the approach we want to take we should import it from one place

import { useRef, useEffect, useMemo, useCallback } from 'react';

// useResizeObserver takes in setWidth and setHeight functions and returns a ref that should be attached to the element you want to observe.
// This hook uses the ResizeObserver API to observe the width and height of a container element.
// The ResizeObserver runs the callback passed as it's argument whenever the observed element's size changes,
// and the effect in this hook attaches the ResizeObserver to the container element when the component mounts, and disconnects it when the component unmounts.

export const useResizeObserver = ({
  setWidth,
  setHeight,
  debounceMs = 16
}) => {
  const containerRef = useRef(null);
  const timeoutIdRef = useRef(null);
  const isMountedRef = useRef(true);
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Create debounced callback
  const debouncedCallback = useCallback(() => {
    return entries => {
      if (!isMountedRef.current) return;
      const {
        width,
        height
      } = entries[0].contentRect;
      const executeCallback = () => {
        if (!isMountedRef.current) return;
        if (containerRef.current && !containerRef.current.isConnected) return;
        if (setWidth) {
          setWidth(width);
        }
        if (setHeight) {
          setHeight(height);
        }
        timeoutIdRef.current = null;
      };
      if (debounceMs > 0) {
        if (timeoutIdRef.current) {
          clearTimeout(timeoutIdRef.current);
        }
        timeoutIdRef.current = setTimeout(executeCallback, debounceMs);
      } else {
        executeCallback();
      }
    };
  }, [setWidth, setHeight, debounceMs]);
  const resizeObserver = useMemo(() => {
    // Only create ResizeObserver if we have at least one callback
    if (!setWidth && !setHeight) return null;
    return new ResizeObserver(debouncedCallback());
  }, [setHeight, setWidth, debouncedCallback]);
  useEffect(() => {
    if (containerRef && containerRef.current && (setWidth || setHeight) && resizeObserver) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [containerRef, setWidth, setHeight, resizeObserver]);
  return containerRef;
};